import { Injectable } from '@angular/core';
import mixpanel from 'mixpanel-browser';

import { environment } from '../../../environments/environment';
import { LocalStorageService } from '../../services/local-storage.service';
import { EventApiService, EventsPayload } from '../../services/events/event-api.service';
import { StonlyEventsListProvider } from '../../services/utils/stonly-events-list-provider';
import { generateUUID, isMobileDevice } from '../../services/utils/device-utils';
import { Event } from '../models/event';
import { TableService } from './table.service';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private events: Event[] = [];
  private eventsMixpanel: Event[] = [];
  private loading = false;
  private session: string = '';
  private isMobile = false;
  private stonlyEvents: string[] = [];
  private collectionInterval: any;

  constructor(
    private eventApiService: EventApiService,
    private localStorageService: LocalStorageService,
    private tableService: TableService
  ) {
    this.stonlyEvents = StonlyEventsListProvider.list;

    // Initialize Mixpanel
    try {
      mixpanel.init('33fdce2b8500301526e8a5ab54d39395');
    } catch (e) {
      console.error('Failed to init mixpanel', e);
    }

    // Initialize session and mobile detection
    this.setSessionInformation();

    // Start listening to Stonly events
    this.startStonlyListener();

    // Start collecting events periodically
    this.startCollectingEvents();
  }

  /**
   * Track an event
   * @param name The event name
   */
  track(name: string): void {
    try {
      const event = this.getEventObject(name);

      this.events.push(event);

      if (mixpanel) {
        this.eventsMixpanel.push(event);
      }
    } catch (e) {
      console.error('Error tracking event:', e);
    }
  }

  /**
   * Creates an event object with metadata
   * @param name The event name
   * @returns Event object
   */
  private getEventObject(name: string): Event {
    const table = this.tableService.getTable();
    const accountTier = table?.installation ?
      (this.getAccountTier(table.installation) || null) :
      null;
    const pipefyPhase = table?.installation ?
      (this.getPipefyPhase(table.installation) || null) :
      null;

    return new Event(
      name,
      environment.version,
      this.isMobile,
      accountTier,
      pipefyPhase
    );
  }

  /**
   * Gets the account tier from installation data
   * @param installation The installation object
   * @returns Account tier string or null
   */
  private getAccountTier(installation: any): string | null {
    if (installation.galaxpay_billing === 1) {
      return 'trial';
    }
    return 'user';
  }

  /**
   * Gets the pipefy phase from installation data
   * @param installation The installation object
   * @returns Pipefy phase string or null
   */
  private getPipefyPhase(installation: any): string | null {
    return installation.pipefy_current_phase || null;
  }

  /**
   * Collect and send events to backend
   */
  private collect(): void {
    if (this.events.length && environment.production) {
      this.loading = true;

      const table = this.tableService.getTable();

      if (!table) {
        this.loading = false;
        return;
      }

      const payload: EventsPayload = {
        session: this.session,
        idi: table.idi,
        user_id: table.idi || null,
        pipefy_phase: this.getPipefyPhase(table.installation),
        events: this.events
      };

      this.eventApiService.sendEvents(payload).subscribe({
        next: () => {
          this.events = [];
          this.loading = false;
        },
        error: (err) => {
          console.error('Error sending events:', err);
          this.loading = false;
        }
      });
    }
  }

  /**
   * Collect and send events to Mixpanel
   */
  private collectMixpanel(): void {
    if (this.eventsMixpanel.length && environment.production) {
      const table = this.tableService.getTable();

      if (!table || !table.idi) {
        return;
      }

      for (const event of this.eventsMixpanel) {
        try {
          mixpanel.track(event.name, {
            ...event,
            session: this.session,
            idi: table.idi,
            user_id: table.id,
            pipefy_phase: this.getPipefyPhase(table.installation)
          });
        } catch (error) {
          console.error('Error sending event to Mixpanel:', error);
        }
      }

      this.eventsMixpanel = [];
    }
  }

  /**
   * Initialize session information
   */
  private setSessionInformation(): void {
    let sessionId = this.localStorageService.getItem<string>('event_session');

    if (!sessionId) {
      sessionId = generateUUID();
      // Session expires in 24 hours
      this.localStorageService.setItem('event_session', sessionId, LocalStorageService.timeToMs.hours(24));
    }

    this.session = sessionId;
    this.isMobile = isMobileDevice();

    // Identify user in Mixpanel if table is loaded
    const table = this.tableService.getTable();
    if (table && table.idi) {
      try {
        mixpanel.identify(table.idi.toString());
      } catch (e) {
        console.error('Error identifying user in Mixpanel:', e);
      }
    }
  }

  /**
   * Start listening to Stonly widget events
   */
  private startStonlyListener(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('message', (event: MessageEvent) => {
      // Listen for Stonly events
      if (event.data && event.data.target && this.stonlyEvents.includes(event.data.target)) {
        this.track(event.data.target);
      }

      // Listen for NPS score events
      if (event.data && event.data.type && event.data.type.includes('npsScoreClicked')) {
        if (event.data.score != null && event.data.score !== undefined) {
          this.track(`nps-score-${event.data.score}`);
        }
      }
    });
  }

  /**
   * Start collecting events periodically (every 6 seconds)
   */
  private startCollectingEvents(): void {
    this.collectionInterval = setInterval(() => {
      const table = this.tableService.getTable();

      if (table && table.idi) {
        if (!this.loading) {
          this.collect();
        }
        this.collectMixpanel();
      }
    }, 6000); // 6 seconds
  }

  /**
   * Clear all pending events
   */
  clear(): void {
    this.events = [];
    this.eventsMixpanel = [];
  }

  /**
   * Destroy the service (cleanup)
   */
  ngOnDestroy(): void {
    if (this.collectionInterval) {
      clearInterval(this.collectionInterval);
    }
  }
}

